import React, { useState } from 'react';
import { View, StyleSheet, FlatList } from 'react-native';
import {
  Text,
  TextInput,
  Card,
  Button,
  Chip,
  useTheme,
  Modal,
  Portal,
  List,
  Checkbox,
} from 'react-native-paper';
import { useFormContext, Controller } from 'react-hook-form';
import { useEquipment } from '@coffeeplexity/api-client';

export function RecipeEquipment() {
  const theme = useTheme();
  const { control, watch, setValue } = useFormContext();

  const [showEquipmentModal, setShowEquipmentModal] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const selectedEquipmentIds = watch('equipment_ids') || [];
  const tags = watch('tags') || [];

  // Fetch equipment data
  const { data: equipmentData, isLoading } = useEquipment({
    search: searchQuery,
    limit: 50,
  });

  const equipment = equipmentData?.items || [];

  const toggleEquipment = (equipmentId: string) => {
    const currentIds = selectedEquipmentIds;
    const isSelected = currentIds.includes(equipmentId);

    if (isSelected) {
      setValue(
        'equipment_ids',
        currentIds.filter((id: string) => id !== equipmentId)
      );
    } else {
      setValue('equipment_ids', [...currentIds, equipmentId]);
    }
  };

  const addTag = (tag: string) => {
    if (tag.trim() && !tags.includes(tag.trim())) {
      setValue('tags', [...tags, tag.trim()]);
    }
  };

  const removeTag = (tagToRemove: string) => {
    setValue(
      'tags',
      tags.filter((tag: string) => tag !== tagToRemove)
    );
  };

  const selectedEquipment = equipment.filter(eq =>
    selectedEquipmentIds.includes(eq.id)
  );

  return (
    <View style={styles.container}>
      {/* Equipment Selection */}
      <Card style={styles.card}>
        <Card.Content>
          <View style={styles.header}>
            <Text variant='headlineSmall' style={styles.sectionTitle}>
              Equipment
            </Text>
            <Button
              mode='contained'
              onPress={() => setShowEquipmentModal(true)}
              icon='plus'
              style={styles.addButton}
              contentStyle={styles.addButtonContent}
              labelStyle={styles.addButtonLabel}
            >
              Add Equipment
            </Button>
          </View>

          {selectedEquipment.length > 0 ? (
            <View style={styles.selectedEquipment}>
              <Text variant='titleMedium' style={styles.subsectionTitle}>
                Selected Equipment ({selectedEquipment.length})
              </Text>
              <FlatList
                data={selectedEquipment}
                keyExtractor={item => item.id}
                renderItem={({ item }) => (
                  <Card style={styles.equipmentCard}>
                    <Card.Content style={styles.equipmentContent}>
                      <View style={styles.equipmentInfo}>
                        <Text variant='titleSmall'>{item.name}</Text>
                        <Text
                          variant='bodySmall'
                          style={{ color: theme.colors.onSurfaceVariant }}
                        >
                          {item.brand} • {item.category}
                        </Text>
                      </View>
                      <Button
                        mode='text'
                        onPress={() => toggleEquipment(item.id)}
                        textColor={theme.colors.error}
                        compact
                      >
                        Remove
                      </Button>
                    </Card.Content>
                  </Card>
                )}
                scrollEnabled={false}
              />
            </View>
          ) : (
            <View style={styles.emptyState}>
              <Text
                variant='bodyMedium'
                style={[
                  styles.emptyText,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                No equipment selected
              </Text>
              <Text
                variant='bodySmall'
                style={[
                  styles.emptyHint,
                  { color: theme.colors.onSurfaceVariant },
                ]}
              >
                Add equipment to help users know what they need
              </Text>
            </View>
          )}
        </Card.Content>
      </Card>

      {/* Tags */}
      <Card style={styles.card}>
        <Card.Content>
          <Text variant='headlineSmall' style={styles.sectionTitle}>
            Tags
          </Text>

          {tags.length > 0 && (
            <View style={styles.tagsContainer}>
              <Text variant='titleMedium' style={styles.subsectionTitle}>
                Current Tags
              </Text>
              <View style={styles.tagsRow}>
                {tags.map((tag: string, index: number) => (
                  <Chip
                    key={index}
                    onClose={() => removeTag(tag)}
                    style={styles.tag}
                  >
                    {tag}
                  </Chip>
                ))}
              </View>
            </View>
          )}

          <Controller
            control={control}
            name='newTag'
            defaultValue=''
            render={({ field: { onChange, value } }) => (
              <View style={styles.tagInput}>
                <TextInput
                  label='Add Tag'
                  value={value}
                  onChangeText={onChange}
                  placeholder='e.g., beginner-friendly, quick, morning'
                  style={styles.input}
                  right={
                    <TextInput.Icon
                      icon='plus'
                      onPress={() => {
                        if (value.trim()) {
                          addTag(value);
                          onChange('');
                        }
                      }}
                    />
                  }
                  onSubmitEditing={() => {
                    if (value.trim()) {
                      addTag(value);
                      onChange('');
                    }
                  }}
                />
              </View>
            )}
          />

          <Text
            variant='bodySmall'
            style={[styles.tagHint, { color: theme.colors.onSurfaceVariant }]}
          >
            Tags help users discover your recipe. Press enter or + to add.
          </Text>
        </Card.Content>
      </Card>

      {/* Equipment Selection Modal */}
      <Portal>
        <Modal
          visible={showEquipmentModal}
          onDismiss={() => setShowEquipmentModal(false)}
          contentContainerStyle={[
            styles.modal,
            { backgroundColor: theme.colors.surface },
          ]}
        >
          <View style={styles.modalHeader}>
            <Text variant='headlineSmall'>Select Equipment</Text>
            <Button onPress={() => setShowEquipmentModal(false)}>Done</Button>
          </View>

          <TextInput
            label='Search Equipment'
            value={searchQuery}
            onChangeText={setSearchQuery}
            style={styles.searchInput}
            left={<TextInput.Icon icon='magnify' />}
          />

          <FlatList
            data={equipment}
            keyExtractor={item => item.id}
            renderItem={({ item }) => (
              <List.Item
                title={item.name}
                description={`${item.brand} • ${item.category}`}
                left={() => (
                  <Checkbox
                    status={
                      selectedEquipmentIds.includes(item.id)
                        ? 'checked'
                        : 'unchecked'
                    }
                    onPress={() => toggleEquipment(item.id)}
                  />
                )}
                onPress={() => toggleEquipment(item.id)}
              />
            )}
            style={styles.equipmentList}
          />
        </Modal>
      </Portal>

      {/* Tips */}
      <Card
        style={[
          styles.card,
          styles.tipsCard,
          { backgroundColor: theme.colors.primaryContainer },
        ]}
      >
        <Card.Content>
          <Text
            variant='titleMedium'
            style={[
              styles.tipsTitle,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            Tips for Equipment & Tags
          </Text>
          <Text
            variant='bodySmall'
            style={[
              styles.tipsText,
              { color: theme.colors.onPrimaryContainer },
            ]}
          >
            • Only include essential equipment{'\n'}• Mention alternatives when
            possible{'\n'}• Use specific, searchable tags{'\n'}• Include skill
            level and time tags{'\n'}• Consider brewing method tags
          </Text>
        </Card.Content>
      </Card>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    paddingBottom: 24,
  },
  card: {
    marginBottom: 16,
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 16,
  },
  sectionTitle: {
    fontWeight: 'bold',
  },
  subsectionTitle: {
    marginBottom: 12,
  },
  selectedEquipment: {
    marginBottom: 16,
  },
  equipmentCard: {
    marginBottom: 8,
    backgroundColor: 'rgba(0,0,0,0.02)',
  },
  equipmentContent: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 8,
  },
  equipmentInfo: {
    flex: 1,
  },
  emptyState: {
    alignItems: 'center',
    paddingVertical: 24,
  },
  emptyText: {
    marginBottom: 4,
  },
  emptyHint: {
    textAlign: 'center',
  },
  tagsContainer: {
    marginBottom: 16,
  },
  tagsRow: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  tag: {
    marginBottom: 4,
  },
  tagInput: {
    marginBottom: 8,
  },
  input: {
    marginBottom: 8,
  },
  tagHint: {
    marginTop: 4,
  },
  modal: {
    margin: 20,
    borderRadius: 8,
    maxHeight: '80%',
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: 'rgba(0,0,0,0.1)',
  },
  searchInput: {
    margin: 16,
    marginBottom: 8,
  },
  equipmentList: {
    flex: 1,
  },
  tipsCard: {
    marginTop: 8,
  },
  tipsTitle: {
    marginBottom: 8,
    fontWeight: 'bold',
  },
  tipsText: {
    lineHeight: 20,
  },
  addButton: {
    borderRadius: 20,
  },
  addButtonContent: {
    height: 40,
    paddingHorizontal: 16,
  },
  addButtonLabel: {
    fontSize: 14,
    lineHeight: 20,
  },
});
